// Logo3D — the app logo rendered as a slowly rotating, extruded 3D object, built purely with CSS:
// a stack of layered copies inside a preserve-3d scene, so no 3D library is pulled in and it stays
// light and themeable. The colourway follows the user's logo preference (same source as LogoMark).
// Used for the startup launch sequence and as the in-app loading indicator. Honors
// prefers-reduced-motion (holds a static angled pose instead of spinning).

import { useMemo, type CSSProperties } from 'react';
import { useSettings } from '../settings/SettingsContext.js';
import { logoDef, type LogoVariant } from '../lib/logos.js';

export function Logo3D({
  size = 96,
  layers = 14,
  depth = 1.3,
  speed = 3.4,
  spin = true,
  glow = true,
  variant,
  className,
  label = 'Loading',
  decorative = false,
}: {
  /** square scene size in px. */
  size?: number;
  /** how many stacked copies form the extrusion (more = smoother, slightly heavier). */
  layers?: number;
  /** z-gap between copies in px — total thickness ≈ layers × depth. */
  depth?: number;
  /** seconds per full turn. */
  speed?: number;
  spin?: boolean;
  glow?: boolean;
  /** force a colourway (defaults to the user's preference). */
  variant?: LogoVariant;
  className?: string;
  label?: string;
  /** beside words that already say it ("Syncing…"): hidden from screen readers */
  decorative?: boolean;
}) {
  const { prefs } = useSettings();
  const def = logoDef(variant ?? prefs.logo);
  const half = (layers - 1) / 2;
  const stack = useMemo(() => Array.from({ length: layers }, (_, i) => i), [layers]);
  return (
    <div
      className={`logo3d${spin ? ' spin' : ''}${className ? ' ' + className : ''}`}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : label}
      aria-hidden={decorative || undefined}
      style={{ width: size, height: size, ['--logo3d-speed']: `${speed}s` } as CSSProperties}
    >
      <div className="logo3d-scene">
        {stack.map((i) => {
          // back copies dim, front copies bright — a cheap directional shade; the front face also
          // carries the colourway glow so the mark reads as a lit, solid object.
          const bright = (0.5 + (0.5 * i) / (layers - 1)).toFixed(3);
          const front = glow && i === layers - 1;
          return (
            <img
              key={i}
              src={def.src}
              alt=""
              aria-hidden="true"
              className="logo3d-layer"
              style={{
                transform: `translateZ(${((i - half) * depth).toFixed(2)}px)`,
                filter: `brightness(${bright})${front ? ` drop-shadow(0 0 10px ${def.glow})` : ''}`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}
